
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent } from "@/components/ui/tabs";

import { AppLayoutWithHeader } from "@/components/layout/AppLayoutWithHeader";
import { AccountSettings } from "@/components/settings/AccountSettings";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import { PasswordSettings } from "@/components/settings/PasswordSettings";
import { EtablissementSettings } from "@/components/settings/EtablissementSettings";
import { TeamManagement } from "@/components/organization/TeamManagement";
import { BrandingManagement } from "@/components/organization/BrandingManagement";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { useOrganizationInitializer } from "@/hooks/useOrganizationInitializer";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2 } from "lucide-react";

export default function Settings() {
  const { tab } = useParams();
  const navigate = useNavigate();
  
  // Load subscription features only when needed to reduce initial latency
  const { canManageMembers, canManageBranding, loading, organizationData, refreshOrganizationData } = useSubscriptionFeatures();
  const { createOrganization, isCreating } = useOrganizationInitializer();
  const activeTab = tab || "account";

  const handleCreateOrganization = async () => {
    const result = await createOrganization();
    if (!result.error) {
      // Rafraîchir les données d'organisation
      await refreshOrganizationData();
    }
  };

  // Redirect invalid tabs to account
  if (!["account", "notifications", "password", "team", "branding", "etablissement"].includes(activeTab)) {
    return <Navigate to="/settings/account" replace />;
  }

  return (
    <AppLayoutWithHeader 
      title="Paramètres"
      subtitle="Gérez vos préférences de compte et vos paramètres de notification"
    >
      {loading ? (
              <div className="space-y-4">
                <div className="flex space-x-1">
                  <Skeleton className="h-12 flex-1" />
                  <Skeleton className="h-12 flex-1" />
                  <Skeleton className="h-12 flex-1" />
                </div>
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <Tabs value={activeTab} className="w-full">
                <div className="w-full inline-flex justify-start items-center gap-6">
                  <div className="flex-1 border-b border-neutral-400/20 flex justify-start items-center gap-7">
                    <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'account' ? 'border-b border-teal-400' : ''}`}>
                      <button
                        className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                          activeTab === 'account' ? 'text-teal-400' : 'text-neutral-400'
                        }`}
                        onClick={() => navigate('/settings/account')}
                      >
                        Mon compte
                      </button>
                    </div>
                    <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'notifications' ? 'border-b border-teal-400' : ''}`}>
                      <button
                        className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                          activeTab === 'notifications' ? 'text-teal-400' : 'text-neutral-400'
                        }`}
                        onClick={() => navigate('/settings/notifications')}
                      >
                        Notifications
                      </button>
                    </div>
                    <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'password' ? 'border-b border-teal-400' : ''}`}>
                      <button
                        className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                          activeTab === 'password' ? 'text-teal-400' : 'text-neutral-400'
                        }`}
                        onClick={() => navigate('/settings/password')}
                      >
                        Mot de passe
                      </button>
                    </div>
                    <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'etablissement' ? 'border-b border-teal-400' : ''}`}>
                      <button
                        className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                          activeTab === 'etablissement' ? 'text-teal-400' : 'text-neutral-400'
                        }`}
                        onClick={() => navigate('/settings/etablissement')}
                      >
                        Mon établissement
                      </button>
                    </div>
                    {canManageMembers && (
                      <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'team' ? 'border-b border-teal-400' : ''}`}>
                        <button
                          className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                            activeTab === 'team' ? 'text-teal-400' : 'text-neutral-400'
                          }`}
                          onClick={() => navigate('/settings/team')}
                        >
                          Équipe
                        </button>
                      </div>
                    )}
                    {canManageBranding && (
                      <div className={`pb-3 flex justify-center items-center gap-2.5 ${activeTab === 'branding' ? 'border-b border-teal-400' : ''}`}>
                        <button
                          className={`justify-start text-sm font-semibold font-['Bricolage_Grotesque'] ${
                            activeTab === 'branding' ? 'text-teal-400' : 'text-neutral-400'
                          }`}
                          onClick={() => navigate('/settings/branding')}
                        >
                          Branding
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              <TabsContent value="account" className="mt-6">
                <AccountSettings />
              </TabsContent>

              <TabsContent value="notifications" className="mt-6">
                <NotificationSettings />
              </TabsContent>

              <TabsContent value="password" className="mt-6">
                <PasswordSettings />
              </TabsContent>

              <TabsContent value="etablissement" className="mt-6">
                <EtablissementSettings />
              </TabsContent>
              
              <TabsContent value="team" className="mt-6">
                {!organizationData ? (
                  <Card className="border-dashed">
                    <CardHeader className="text-center">
                      <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                        <Building2 className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <CardTitle>Aucune organisation trouvée</CardTitle>
                      <CardDescription>
                        Pour accéder aux fonctionnalités d'équipe et de branding, vous devez créer une organisation.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <Button onClick={handleCreateOrganization} disabled={isCreating}>
                        {isCreating ? "Création en cours..." : "Créer une organisation"}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <TeamManagement />
                )}
              </TabsContent>

              <TabsContent value="branding" className="mt-6">
                {!organizationData ? (
                  <Card className="border-dashed">
                    <CardHeader className="text-center">
                      <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                        <Building2 className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <CardTitle>Aucune organisation trouvée</CardTitle>
                      <CardDescription>
                        Pour accéder aux fonctionnalités d'équipe et de branding, vous devez créer une organisation.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <Button onClick={handleCreateOrganization} disabled={isCreating}>
                        {isCreating ? "Création en cours..." : "Créer une organisation"}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <BrandingManagement />
                )}
              </TabsContent>
        </Tabs>
      )}
    </AppLayoutWithHeader>
  );
}